import { useEffect, useState } from "react";
import { loadBlockedUsers, unblockUser, type BlockedUser } from "../apiClient.js";
import { Avatar } from "./Avatar.js";
import { useT } from "../lib/i18n/store.js";

/**
 * "Blocked users" management for the author's dashboard: lists the users the
 * author has blocked from commenting, each with an unblock button. Owns its own
 * data. Renders nothing while empty (there is nothing to manage), so it stays
 * out of the way until the author has actually blocked someone.
 */
export function BlockedUsersManager() {
  const t = useT();
  const [blocked, setBlocked] = useState<BlockedUser[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void loadBlockedUsers()
      .then((list) => {
        if (!cancelled) setBlocked(list);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleUnblock(user: BlockedUser) {
    try {
      await unblockUser(user.user_id);
      setBlocked((current) => current.filter((u) => u.user_id !== user.user_id));
    } catch {
      // Leave the row; a later action or reload reconciles.
    }
  }

  if (!loaded || blocked.length === 0) return null;

  return (
    <section className="blocked-users">
      <h2 className="blocked-users-heading">{t("blocked.heading", { count: blocked.length })}</h2>
      <ul className="blocked-list">
        {blocked.map((user) => (
          <li key={user.user_id} className="blocked-row">
            <Avatar name={user.display_name} url={user.avatar_url} size={28} className="blocked-avatar" />
            <span className="blocked-name">{user.display_name}</span>
            <button type="button" className="secondary" onClick={() => void handleUnblock(user)}>
              {t("blocked.unblock")}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
